/**
 * App shell: auth gate, tab navigation, sync lifecycle.
 *
 * There is no router library here on purpose. The app has five tabs and no
 * deep links worth preserving; react-router would add a dependency and a
 * history-vs-WebView edge case to debug for no benefit. If plan sharing later
 * needs real URLs, that's the moment to add it.
 */

import { useCallback, useEffect, useState } from 'react'

import {
  api,
  ApiError,
  fetchMe,
  getToken,
  login,
  logout,
  type User,
} from './api/client'
import { OWNER_KEY, getMeta, setMeta, wipeLocal } from './db'
import { hydrateToken } from './native/storage'
import { startSyncLoop, subscribe, sync, type SyncState } from './sync/engine'
import { LogScreen } from './screens/Log'
import { HistoryScreen } from './screens/History'
import { PlanScreen } from './screens/Plan'
import { ProgressScreen } from './screens/Progress'
import { SettingsScreen } from './screens/Settings'
import { AdminScreen } from './screens/Admin'
import { UpdateBanner } from './components/UpdateBanner'
import { ErrorBoundary } from './components/ErrorBoundary'

type Tab = 'log' | 'plan' | 'progress' | 'history' | 'settings' | 'admin'

export default function App() {
  const [user, setUser] = useState<User | null>(null)
  const [booting, setBooting] = useState(true)
  const [tab, setTab] = useState<Tab>('log')

  // Boot: recover a session if there is one, then verify it.
  //
  // Every step here is bounded. A native call that hangs, a server that is
  // unreachable, a revoked token — all of them must end with the sign-in
  // screen, never with a blank one. Booting is a state we always leave.
  useEffect(() => {
    let cancelled = false

    const finish = () => { if (!cancelled) setBooting(false) }

    void (async () => {
      // The WebView can lose localStorage while Capacitor Preferences survives.
      // Bounded: if the native bridge isn't ready this resolves as "no stored
      // session" rather than hanging the app forever.
      try {
        await Promise.race([
          hydrateToken(),
          new Promise((resolve) => setTimeout(resolve, 2000)),
        ])
      } catch {
        /* no native storage, or it failed — fall through to the token we have */
      }

      if (cancelled) return

      if (!getToken()) {
        finish()
        return
      }

      try {
        const me = await fetchMe()
        if (!cancelled) setUser(me)
      } catch {
        if (!cancelled) setUser(null)
      } finally {
        finish()
      }
    })()

    return () => { cancelled = true }
  }, [])

  // Start syncing once, after sign-in.
  useEffect(() => {
    if (!user) return
    return startSyncLoop()
  }, [user?.id])

  // Tell the server where this device thinks it is.
  //
  // The server decides what "today" means — which square the calendar outlines,
  // whether a slot has been missed, which day an evening workout belongs to —
  // and it can only get that right if it knows the zone. It defaulted to
  // America/Toronto and was never told otherwise, so a wrong default would have
  // been invisible until someone travelled.
  useEffect(() => {
    if (!user) return
    let zone: string | undefined
    try {
      zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch {
      return                       // no Intl data; the server default stands
    }
    if (!zone || zone === user.timezone) return
    api<{ user: User }>('/auth/me', { method: 'PATCH', body: { timezone: zone } })
      .then((r) => setUser(r.user))
      .catch(() => { /* offline; it'll correct itself next launch */ })
  }, [user?.id, user?.timezone])

  const handleSignedIn = useCallback(async (u: User) => {
    // If this device previously held a different account's data, wipe it.
    // Without this the new account inherits the old one's sessions locally and
    // then pushes them to the server under the wrong owner.
    const previous = await getMeta<number | null>(OWNER_KEY, null)
    if (previous !== null && previous !== u.id) await wipeLocal()
    await setMeta(OWNER_KEY, u.id)
    setUser(u)
  }, [])

  if (booting) {
    return (
      <div className="wrap">
        <p className="muted">Loading…</p>
      </div>
    )
  }

  if (!user) return <SignIn onSignedIn={handleSignedIn} />

  return (
    <>
      <UpdateBanner />
      <SyncBanner />
      {/* The boundary goes HERE, not around the whole app.

          1.0.756375 crashed on render in LogScreen, which is the default tab. The
          only boundary was the one in main.tsx wrapping <App/>, so the crash
          replaced the banner and the tab bar along with the screen — and Reload
          came back to the same default tab and crashed again. The app could not
          be updated out of its own bug from inside itself, which for a sideloaded
          app with no store is close to bricked.

          Scoped here, one screen failing costs you that screen. The update banner
          survives to offer the fix, and the tab bar survives to reach Settings.
          Keyed by tab so switching away from the broken screen clears the error
          rather than sticking to it. main.tsx keeps its boundary as the backstop
          for anything that throws above this point. */}
      <ErrorBoundary key={tab}>
      <div className="wrap" style={{ paddingBottom: '5.5rem' }}>
        {tab === 'log' && (
          <LogScreen
            unit={user.unit_pref}
            distancePref={user.distance_pref}
            defaultRest={user.default_rest_seconds}
            username={user.username}
          />
        )}
        {tab === 'plan' && (
          <PlanScreen
            unit={user.unit_pref}
            distancePref={user.distance_pref}
            onStarted={() => setTab('log')}
          />
        )}
        {tab === 'progress' && (
          <ProgressScreen unit={user.unit_pref} distancePref={user.distance_pref} />
        )}
        {tab === 'history' && (
          <HistoryScreen unit={user.unit_pref} distancePref={user.distance_pref} />
        )}
        {tab === 'admin' && user.is_admin && <AdminScreen user={user} />}
        {tab === 'settings' && (
          <SettingsScreen
            user={user}
            onUserChange={setUser}
            onSignOut={async () => {
              await logout()
              setUser(null)
            }}
          />
        )}
      </div>
      </ErrorBoundary>
      <TabBar tab={tab} onChange={setTab} isAdmin={user.is_admin} />
    </>
  )
}

/**
 * Offline indicator.
 *
 * Shown only when offline with work queued — not merely "offline". Logging
 * works fine with no signal, so a permanent warning would train the user to
 * ignore the banner. It says "saved here", because the fear it needs to answer
 * is "am I losing this?".
 */
function SyncBanner() {
  const [state, setState] = useState<SyncState | null>(null)
  useEffect(() => subscribe(setState), [])

  if (!state || state.online) return null

  return (
    <div
      style={{
        background: 'var(--surface-2)',
        borderBottom: '1px solid var(--border)',
        padding: '.5rem 1rem',
        fontSize: '.85rem',
        textAlign: 'center',
      }}
    >
      Offline — {state.pending > 0
        ? `${state.pending} change${state.pending === 1 ? '' : 's'} saved on this device`
        : 'your workout is saved on this device'}
    </div>
  )
}

function TabBar({
  tab,
  onChange,
  isAdmin,
}: {
  tab: Tab
  onChange: (t: Tab) => void
  isAdmin: boolean
}) {
  const tabs: { id: Tab; label: string }[] = [
    { id: 'log', label: 'Log' },
    { id: 'plan', label: 'Plan' },
    { id: 'progress', label: 'Progress' },
    { id: 'history', label: 'History' },
    { id: 'settings', label: 'More' },
    // Only for admins. Six tabs is the practical ceiling on a narrow phone,
    // which is why this is conditional rather than always present and empty.
    ...(isAdmin ? [{ id: 'admin' as Tab, label: 'Admin' }] : []),
  ]
  return (
    <nav
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        background: 'var(--surface)',
        borderTop: '1px solid var(--border)',
        // Clears the Android gesture bar.
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            flex: 1,
            margin: 0,
            borderRadius: 0,
            border: 'none',
            background: 'transparent',
            color: tab === t.id ? 'var(--accent)' : 'var(--muted)',
            fontWeight: tab === t.id ? 600 : 400,
            minHeight: 52,
            fontSize: '.76rem',
            padding: 0,
            minWidth: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {t.label}
        </button>
      ))}
    </nav>
  )
}

function SignIn({ onSignedIn }: { onSignedIn: (u: User) => void }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setBusy(true)
      setError(null)
      try {
        const u = await login(username, password, describeDevice())
        await onSignedIn(u)
        void sync({ reference: true })
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : "Couldn't reach the server. Check your connection.",
        )
      } finally {
        setBusy(false)
      }
    },
    [username, password, onSignedIn],
  )

  return (
    <div className="wrap" style={{ maxWidth: '24rem' }}>
      <h1>Lift</h1>
      <form className="card" onSubmit={submit}>
        {error && <div className="error">{error}</div>}

        <label htmlFor="username">Username</label>
        <input
          id="username"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
        />

        <label htmlFor="password">Password</label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        <button type="submit" style={{ width: '100%' }} disabled={busy || !username || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <p className="muted" style={{ marginTop: '1.5rem' }}>
        Lift is invite-only. If someone sent you an invite link, open it in a
        browser to create your account first.
      </p>
    </div>
  )
}

function describeDevice(): string {
  const ua = navigator.userAgent
  if (/Android/i.test(ua)) return 'Android'
  if (/iPhone|iPad/i.test(ua)) return 'iOS'
  if (/Macintosh/i.test(ua)) return 'Mac browser'
  if (/Windows/i.test(ua)) return 'Windows browser'
  return 'Browser'
}
